import type { UpcomingEvent } from "../models/types";
import { birthdayRemindersEnabled } from "../storage/session";

const REMINDER_HOUR = 9;

export async function requestNotificationPermission(): Promise<NotificationPermission | "unsupported"> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return Notification.requestPermission();
}

export function scheduleBirthdayReminders(events: UpcomingEvent[]): void {
  if (!birthdayRemindersEnabled()) return;
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;

  const key = "sundius_scheduled_reminders";
  const scheduled = new Set<string>(
    JSON.parse(localStorage.getItem(key) ?? "[]") as string[],
  );

  for (const event of events) {
    if (event.kind !== "birthday" || event.is_deceased) continue;
    if (event.days_until < 0 || event.days_until > 14) continue;
    const reminderKey = `${event.id}:${event.occurs_on}`;
    if (scheduled.has(reminderKey)) continue;

    const when = new Date(event.occurs_on);
    when.setHours(REMINDER_HOUR, 0, 0, 0);
    const now = Date.now();
    const delay = when.getTime() - now;
    if (delay <= 0 || delay > 14 * 24 * 60 * 60 * 1000) continue;

    window.setTimeout(() => {
      if (!birthdayRemindersEnabled()) return;
      if (Notification.permission !== "granted") return;
      try {
        new Notification("Sundius Family", {
          body: `${event.label} is today!`,
          tag: reminderKey,
        });
      } catch {
        /* service worker may be required on some platforms */
      }
    }, delay);

    scheduled.add(reminderKey);
  }

  localStorage.setItem(key, JSON.stringify([...scheduled].slice(-100)));
}

export async function subscribeWebPush(sessionToken: string): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await registerPushSubscription(sessionToken, existing);
      return true;
    }
    const res = await fetch("/sundius/family/push/vapid-public-key");
    if (!res.ok) return false;
    const { publicKey } = (await res.json()) as { publicKey: string };
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await registerPushSubscription(sessionToken, sub);
    return true;
  } catch {
    return false;
  }
}

async function registerPushSubscription(
  sessionToken: string,
  sub: PushSubscription,
): Promise<void> {
  const json = sub.toJSON();
  await fetch("/sundius/family/push/subscribe", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
    }),
  });
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
